import { useEffect, useMemo, useState } from 'react';
import {
  approveServiceSpecSubmission,
  deleteRejectedServiceSpecSubmission,
  getAdminServiceSpecs,
  getAdminServiceSpecSubmissions,
  rejectServiceSpecSubmission,
  updateAdminServiceSpec,
} from '../../parts/api';

const formatJson = (value) => JSON.stringify(value || [], null, 2);
const SpecEditor = ({ value, onChange }) => {
  const set = (field, next) => onChange({ ...value, [field]: next });
  const setNested = (group, field, next) => onChange({ ...value, [group]: { ...(value[group] || {}), [field]: next } });
  const setJson = (field, raw) => {
    try { set(field, JSON.parse(raw)); } catch (_) { set(`_${field}Draft`, raw); }
  };
  return (
    <div className="admin-service-spec-editor">
      <div className="admin-service-spec-grid">
        <label>Area<input value={value.pilotArea || ''} onChange={(e) => set('pilotArea', e.target.value)} /></label>
        <label>Type<input value={value.specType || ''} onChange={(e) => set('specType', e.target.value)} /></label>
        <label>Component<input value={value.component || ''} onChange={(e) => set('component', e.target.value)} /></label>
        <label>Connection<input value={value.connection || ''} onChange={(e) => set('connection', e.target.value)} /></label>
        <label>Fastener type<input value={value.fastener?.type || ''} onChange={(e) => setNested('fastener', 'type', e.target.value)} /></label>
        <label>Fastener size<input value={value.fastener?.size || ''} onChange={(e) => setNested('fastener', 'size', e.target.value)} /></label>
        <label>Year start<input type="number" value={value.applicability?.yearStart || ''} onChange={(e) => setNested('applicability', 'yearStart', e.target.value ? Number(e.target.value) : undefined)} /></label>
        <label>Year end<input type="number" value={value.applicability?.yearEnd || ''} onChange={(e) => setNested('applicability', 'yearEnd', e.target.value ? Number(e.target.value) : undefined)} /></label>
        <label>Source document<input value={value.source?.document || ''} onChange={(e) => setNested('source', 'document', e.target.value)} /></label>
        <label>Source page<input type="number" min="1" value={value.source?.pdfPage || ''} onChange={(e) => setNested('source', 'pdfPage', Number(e.target.value))} /></label>
        <label className="admin-service-spec-wide">Manual section<input value={value.source?.manualSection || ''} onChange={(e) => setNested('source', 'manualSection', e.target.value)} /></label>
        <label className="admin-service-spec-wide">Exact source quote<textarea rows={3} value={value.source?.quote || ''} onChange={(e) => setNested('source', 'quote', e.target.value)} /></label>
        <label className="admin-service-spec-wide">Steps (JSON array)<textarea rows={6} value={value._stepsDraft ?? formatJson(value.steps)} onChange={(e) => setJson('steps', e.target.value)} /></label>
        <label className="admin-service-spec-wide">Conditions (JSON array)<textarea rows={3} value={value._conditionsDraft ?? formatJson(value.conditions)} onChange={(e) => setJson('conditions', e.target.value)} /></label>
        <label className="admin-service-spec-wide">Cautions (JSON array)<textarea rows={3} value={value._cautionsDraft ?? formatJson(value.cautions)} onChange={(e) => setJson('cautions', e.target.value)} /></label>
        <label className="admin-service-spec-wide">Applicability qualifiers (JSON array)<textarea rows={3} value={value._qualifiersDraft ?? formatJson(value.applicability?.qualifiers)} onChange={(e) => {
          try { setNested('applicability', 'qualifiers', JSON.parse(e.target.value)); } catch (_) { set('_qualifiersDraft', e.target.value); }
        }} /></label>
      </div>
    </div>
  );
};

const cleanDraftFields = (value) => {
  const clean = JSON.parse(JSON.stringify(value));
  Object.keys(clean).filter((key) => key.startsWith('_')).forEach((key) => delete clean[key]);
  return clean;
};

const ServiceSpecsAdmin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('');
  const [submissionDraft, setSubmissionDraft] = useState(null);
  const [selectedSpecKey, setSelectedSpecKey] = useState('');
  const [specDraft, setSpecDraft] = useState(null);
  const [filter, setFilter] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [submissionPayload, specPayload] = await Promise.all([
        getAdminServiceSpecSubmissions({ status: 'all' }),
        getAdminServiceSpecs(),
      ]);
      setSubmissions(submissionPayload.submissions || []);
      setSpecifications(specPayload.specifications || []);
      const requested = new URLSearchParams(window.location.search).get('submission');
      const firstPending = (submissionPayload.submissions || []).find((item) => item.status === 'pending');
      const selected = (submissionPayload.submissions || []).find((item) => item._id === requested) || firstPending;
      if (selected) {
        setSelectedSubmissionId(selected._id);
        setSubmissionDraft(JSON.parse(JSON.stringify(selected.proposedSpecification)));
      }
      if (requested) {
        window.setTimeout(() => document.getElementById('service-specs')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
      }
    } catch (error) {
      setStatus(error.message || 'Failed to load Service Specs administration.');
    }
  };

  useEffect(() => { load(); }, []);

  const selectSubmission = (submission) => {
    setSelectedSubmissionId(submission._id);
    setSubmissionDraft(JSON.parse(JSON.stringify(submission.proposedSpecification)));
    setReviewNote(submission.review?.note || '');
    setStatus('');
  };
  const selectedSubmission = submissions.find((item) => item._id === selectedSubmissionId);

  const approve = async () => {
    if (!selectedSubmission || !submissionDraft) return;
    try {
      setBusy(true); setStatus('');
      await approveServiceSpecSubmission({ id: selectedSubmission._id, proposedSpecification: cleanDraftFields(submissionDraft), note: reviewNote });
      setStatus('Submission approved and published.');
      await load();
    } catch (error) { setStatus(error.message || 'Approval failed.'); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!selectedSubmission) return;
    try {
      setBusy(true); setStatus('');
      await rejectServiceSpecSubmission({ id: selectedSubmission._id, note: reviewNote });
      setStatus('Submission rejected.');
      await load();
    } catch (error) { setStatus(error.message || 'Rejection failed.'); } finally { setBusy(false); }
  };
  const deleteRejected = async () => {
    if (!selectedSubmission || selectedSubmission.status !== 'rejected') return;
    if (!window.confirm('Permanently delete this rejected submission?')) return;
    try {
      setBusy(true); setStatus('');
      await deleteRejectedServiceSpecSubmission({ id: selectedSubmission._id });
      setSubmissions((current) => current.filter((item) => item._id !== selectedSubmission._id));
      setSelectedSubmissionId('');
      setSubmissionDraft(null);
      setReviewNote('');
      setStatus('Rejected submission deleted.');
    } catch (error) { setStatus(error.message || 'Deletion failed.'); } finally { setBusy(false); }
  };

  const filteredSpecs = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return specifications;
    return specifications.filter((spec) => [spec.specKey, spec.component, spec.connection, spec.pilotArea].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [filter, specifications]);

  const selectSpec = (spec) => {
    setSelectedSpecKey(spec.specKey);
    setSpecDraft(JSON.parse(JSON.stringify(spec)));
    setStatus('');
  };
  const saveSpec = async () => {
    if (!selectedSpecKey || !specDraft) return;
    try {
      setBusy(true); setStatus('');
      const payload = await updateAdminServiceSpec({ specKey: selectedSpecKey, specification: cleanDraftFields(specDraft) });
      setSpecifications((current) => current.map((item) => item.specKey === selectedSpecKey ? payload.specification : item));
      setSpecDraft(JSON.parse(JSON.stringify(payload.specification)));
      setStatus('Service Spec saved.');
    } catch (error) { setStatus(error.message || 'Failed to save Service Spec.'); } finally { setBusy(false); }
  };

  const pendingCount = submissions.filter((item) => item.status === 'pending').length;
  return (
    <section className="admin-service-specs" id="service-specs">
      <div className="admin-service-spec-title">
        <div><h3>Service Specs moderation</h3><p>Review community contributions or edit any published Service Spec.</p></div>
        <span>{pendingCount} pending</span>
      </div>
      {status && <div className={status.includes('failed') || status.includes('Failed') ? 'error-banner' : 'success-banner'}>{status}</div>}

      <div className="admin-service-spec-layout">
        <div className="admin-service-spec-list">
          <h4>Community submissions</h4>
          {submissions.length === 0 && <p>No submissions yet.</p>}
          {submissions.map((item) => (
            <button type="button" key={item._id} className={item._id === selectedSubmissionId ? 'selected' : ''} onClick={() => selectSubmission(item)}>
              <strong>{item.submissionType === 'correction' ? 'Correction' : 'Missing spec'} · {item.target.partNumber || item.target.position}</strong>
              <span>{item.proposedSpecification.connection}</span>
              <small>{item.submitter.email} · {item.status}</small>
            </button>
          ))}
        </div>
        <div className="admin-service-spec-detail">
          {!selectedSubmission && <p>Select a submission to review it.</p>}
          {selectedSubmission && submissionDraft && (
            <>
              <h4>{selectedSubmission.submissionType === 'correction' ? 'Review correction' : 'Review missing spec'}</h4>
              <p><strong>Target:</strong> {selectedSubmission.target.modelSlug} / {selectedSubmission.target.sectionCode} / {selectedSubmission.target.position} / {selectedSubmission.target.partNumber || 'no part number'}</p>
              <p><strong>Contributor’s reason:</strong> {selectedSubmission.reason}</p>
              <SpecEditor value={submissionDraft} onChange={setSubmissionDraft} />
              <label>Admin review note<textarea rows={2} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} /></label>
              <div className="request-actions">
                <button type="button" onClick={approve} disabled={busy || selectedSubmission.status !== 'pending'}>Approve and publish</button>
                <button type="button" onClick={reject} disabled={busy || selectedSubmission.status !== 'pending'}>Reject</button>
                {selectedSubmission.status === 'rejected' && (
                  <button type="button" className="admin-service-spec-delete" onClick={deleteRejected} disabled={busy}>Delete rejected submission</button>
                )}
                <span className="request-status">{selectedSubmission.status}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="admin-service-spec-published">
        <h4>Edit published Service Specs</h4>
        <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by key, component, connection, or area" />
        <div className="admin-service-spec-layout">
          <div className="admin-service-spec-list published">
            {filteredSpecs.map((spec) => (
              <button type="button" key={spec.specKey} className={spec.specKey === selectedSpecKey ? 'selected' : ''} onClick={() => selectSpec(spec)}>
                <strong>{spec.component}</strong><span>{spec.connection}</span><small>{spec.specKey}</small>
              </button>
            ))}
          </div>
          <div className="admin-service-spec-detail">
            {!specDraft && <p>Select any published spec to edit it.</p>}
            {specDraft && <><h4>{selectedSpecKey}</h4><SpecEditor value={specDraft} onChange={setSpecDraft} /><button type="button" onClick={saveSpec} disabled={busy}>Save Service Spec</button></>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSpecsAdmin;
